import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  TextField,
  Tabs,
  Tab,
  CircularProgress,
  IconButton,
  Chip,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import { marketingPersonApi } from '../../../modules/services/marketingPersonService';
import { downloadSingleMarketingPersonExcel } from '../../../common/downloadMarketingPersonsExcel';
import translate from '../../../i18n/translate';

interface Props {
  open: boolean;
  onClose: () => void;
  personId: number;
  personName: string;
}

const gridSx = {
  backgroundColor: '#ede9e4',
  borderRadius: '0px',
  border: 'none',
  '& .MuiDataGrid-virtualScroller': { marginTop: '0 !important' },
  '& .MuiDataGrid-main': { overflow: 'visible' },
  '.MuiDataGrid-columnHeaders': { backgroundColor: 'warning.main', position: 'sticky', zIndex: 2, borderRadius: '0px', border: '0px' },
  '.MuiDataGrid-columnHeaderTitleContainer': { color: '#fff', fontSize: '14px', padding: '8px', display: 'flex', alignContent: 'center', justifyContent: 'center' },
  '.MuiDataGrid-cell': { border: '1px solid #fc7f03', padding: '5px', display: 'flex', alignContent: 'center', justifyContent: 'center' },
  '.MuiDataGrid-footerContainer': { border: '1px solid #fc7f03' },
  '.MuiDataGrid-footerContainer p': { margin: '0px' },
  '.MuiDataGrid-columnHeaderTitle': { overflow: 'visible', lineHeight: '1.43rem', whiteSpace: 'normal' },
};

const PersonReportModal: React.FC<Props> = ({ open, onClose, personId, personName }) => {
  const [tab, setTab] = useState(0);
  const [startPeriod, setStartPeriod] = useState('');
  const [endPeriod, setEndPeriod] = useState('');

  const filters = useMemo(() => ({
    marketingPersonId: personId,
    startPeriod: startPeriod || undefined,
    endPeriod: endPeriod || undefined,
  }), [personId, startPeriod, endPeriod]);

  const { data: byOrganizerData = [], isFetching: orgFetching } =
    marketingPersonApi.useGetMarketingPersonsReportQuery(filters, { skip: !open });

  const { data: byEventsData = [], isFetching: eventsFetching } =
    marketingPersonApi.useGetMarketingPersonsReportByEventsQuery(filters, { skip: !open });

  type StatRow = { ticketCount: number; totalAmount: number; totalFee: number; totalCommission: number };
  const activeData: StatRow[] = tab === 0 ? byOrganizerData : byEventsData;
  const totalTickets = activeData.reduce((s, r) => s + (r.ticketCount || 0), 0);
  const totalAmount = activeData.reduce((s, r) => s + (r.totalAmount || 0), 0);
  const totalFee = activeData.reduce((s, r) => s + (r.totalFee || 0), 0);
  const totalCommission = activeData.reduce((s, r) => s + (r.totalCommission || 0), 0);

  const infoColumns = [
    { field: 'organizerName', headerName: translate('marketingPersonsGridOrganizerName'), flex: 2, minWidth: 160 },
    { field: 'ticketCount', headerName: translate('marketingPersonsTotalTickets'), flex: 1, minWidth: 80 },
    {
      field: 'totalAmount',
      headerName: translate('marketingPersonsTotalAmount'),
      flex: 1, minWidth: 110,
      renderCell: (params: any) => <span>₪{(params.row.totalAmount || 0).toLocaleString()}</span>,
    },
    {
      field: 'totalFee',
      headerName: translate('marketingPersonsGridTotalFee'),
      flex: 1, minWidth: 110,
      renderCell: (params: any) => (
        <Typography variant="body2" color="warning.main" fontWeight="bold">
          ₪{(params.row.totalFee || 0).toLocaleString()}
        </Typography>
      ),
    },
    {
      field: 'totalCommission',
      headerName: translate('marketingPersonsGridTotalCommission'),
      flex: 1, minWidth: 110,
      renderCell: (params: any) => (
        <Typography variant="body2" color="success.main" fontWeight="bold">
          ₪{(params.row.totalCommission || 0).toLocaleString()}
        </Typography>
      ),
    },
    {
      field: 'commissionType',
      headerName: translate('marketingPersonsGridCommissionType'),
      flex: 0.8, minWidth: 90,
      renderCell: (params: any) => (
        <Chip
          label={params.row.commissionType === 'percent' ? '%' : '₪'}
          size="small"
          color={params.row.commissionType === 'percent' ? 'info' : 'warning'}
          variant="outlined"
        />
      ),
    },
    {
      field: 'commissionValue',
      headerName: translate('marketingPersonsGridCommissionValue'),
      flex: 0.7, minWidth: 80,
      renderCell: (params: any) => (
        <Typography variant="body2" fontWeight="bold">
          {params.row.commissionValue}{params.row.commissionType === 'percent' ? '%' : ' ₪'}
        </Typography>
      ),
    },
    { field: 'eventsCount', headerName: translate('marketingPersonsEventsCount'), flex: 1, minWidth: 80 },
    {
      field: 'workStartDate',
      headerName: translate('marketingPersonsGridStartDate'),
      flex: 1, minWidth: 100,
      renderCell: (params: any) => <span>{params.row.workStartDate ? new Date(params.row.workStartDate).toLocaleDateString() : '-'}</span>,
    },
    {
      field: 'workEndDate',
      headerName: translate('marketingPersonsGridEndDate'),
      flex: 1, minWidth: 100,
      renderCell: (params: any) => <span>{params.row.workEndDate ? new Date(params.row.workEndDate).toLocaleDateString() : '-'}</span>,
    },
  ];

  const eventsColumns = [
    { field: 'organizerName', headerName: translate('marketingPersonsGridOrganizerName'), flex: 2, minWidth: 140 },
    { field: 'eventId', headerName: 'ID', flex: 0.5, minWidth: 55 },
    { field: 'eventName', headerName: translate('marketingPersonsGridEventName'), flex: 3, minWidth: 180 },
    {
      field: 'eventStartDate',
      headerName: translate('marketingPersonsGridEventDate'),
      flex: 1, minWidth: 100,
      renderCell: (params: any) => <span>{params.row.eventStartDate ? new Date(params.row.eventStartDate).toLocaleDateString() : '-'}</span>,
    },
    { field: 'ticketCount', headerName: translate('marketingPersonsTotalTickets'), flex: 1, minWidth: 75 },
    {
      field: 'totalAmount',
      headerName: translate('marketingPersonsTotalAmount'),
      flex: 1, minWidth: 105,
      renderCell: (params: any) => <span>₪{(params.row.totalAmount || 0).toLocaleString()}</span>,
    },
    {
      field: 'totalFee',
      headerName: translate('marketingPersonsGridTotalFee'),
      flex: 1, minWidth: 100,
      renderCell: (params: any) => (
        <Typography variant="body2" color="warning.main" fontWeight="bold">
          ₪{(params.row.totalFee || 0).toLocaleString()}
        </Typography>
      ),
    },
    {
      field: 'totalCommission',
      headerName: translate('marketingPersonsGridTotalCommission'),
      flex: 1, minWidth: 105,
      renderCell: (params: any) => (
        <Typography variant="body2" color="success.main" fontWeight="bold">
          ₪{(params.row.totalCommission || 0).toLocaleString()}
        </Typography>
      ),
    },
    {
      field: 'commissionType',
      headerName: translate('marketingPersonsGridCommissionType'),
      flex: 0.8, minWidth: 90,
      renderCell: (params: any) => (
        <Chip
          label={params.row.commissionType === 'percent' ? '%' : '₪'}
          size="small"
          color={params.row.commissionType === 'percent' ? 'info' : 'warning'}
          variant="outlined"
        />
      ),
    },
    {
      field: 'commissionValue',
      headerName: translate('marketingPersonsGridCommissionValue'),
      flex: 0.7, minWidth: 80,
      renderCell: (params: any) => (
        <Typography variant="body2" fontWeight="bold">
          {params.row.commissionValue}{params.row.commissionType === 'percent' ? '%' : ' ₪'}
        </Typography>
      ),
    },
  ];

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: 'warning.main' }}>
        {translate('marketingPersonsReport')}: {personName}
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pb: 0 }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
          <DatePicker
            label={translate('marketingPersonsPeriodStart')}
            value={startPeriod ? dayjs(startPeriod) : null}
            onChange={(v: Dayjs | null) => setStartPeriod(v ? v.format('YYYY-MM-DD') : '')}
            renderInput={(params) => <TextField {...params} size="small" color="warning" />}
          />
          <DatePicker
            label={translate('marketingPersonsPeriodEnd')}
            value={endPeriod ? dayjs(endPeriod) : null}
            onChange={(v: Dayjs | null) => setEndPeriod(v ? v.format('YYYY-MM-DD') : '')}
            renderInput={(params) => <TextField {...params} size="small" color="warning" />}
          />
          <Button
            size="small"
            color="warning"
            startIcon={<DownloadIcon />}
            onClick={() => downloadSingleMarketingPersonExcel(personId, {
              startPeriod: startPeriod || undefined,
              endPeriod: endPeriod || undefined,
            })}
          >
            {translate('marketingPersonsDownloadAllExcel')}
          </Button>
        </div>

        {activeData.length > 0 && (
          <div style={{ display: 'flex', gap: 16, marginBottom: 12, flexWrap: 'wrap' }}>
            {[
              { label: translate('marketingPersonsTotalTickets'), value: String(totalTickets), color: 'warning.main' },
              { label: translate('marketingPersonsTotalAmount'), value: `₪${totalAmount.toLocaleString()}`, color: 'warning.main' },
              { label: translate('marketingPersonsTotalFee'), value: `₪${totalFee.toLocaleString()}`, color: 'warning.main' },
              { label: translate('marketingPersonsTotalCommission'), value: `₪${totalCommission.toLocaleString()}`, color: 'success.main' },
            ].map((stat) => (
              <div key={stat.label as any} style={{ background: '#fff8f0', border: '1px solid #fc7f03', borderRadius: 6, padding: '8px 16px', minWidth: 120 }}>
                <Typography variant="caption" color="textSecondary" display="block">{stat.label}</Typography>
                <Typography variant="h6" color={stat.color} fontWeight="bold">{stat.value}</Typography>
              </div>
            ))}
          </div>
        )}

        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{ mb: 1, '& .MuiTab-root': { color: 'warning.main' }, '& .Mui-selected': { color: 'warning.dark' } }}
          TabIndicatorProps={{ sx: { backgroundColor: 'warning.main' } }}
        >
          <Tab label={translate('marketingPersonsReportByOrganizer')} />
          <Tab label={translate('marketingPersonsReportByEvents')} />
        </Tabs>

        {tab === 0 && (
          orgFetching ? <CircularProgress color="warning" /> : (
            <DataGrid
              sx={gridSx}
              rows={byOrganizerData.map((r, i) => ({ ...r, id: `${r.organizerId}-${i}` }))}
              columns={infoColumns as unknown as GridColDef[]}
              getRowHeight={() => 'auto'}
              rowsPerPageOptions={[10, 25, 50]}
              autoHeight
              density="comfortable"
            />
          )
        )}

        {tab === 1 && (
          eventsFetching ? <CircularProgress color="warning" /> : (
            <DataGrid
              sx={gridSx}
              rows={byEventsData.map((r, i) => ({ ...r, id: `${r.eventId}-${i}` }))}
              columns={eventsColumns as unknown as GridColDef[]}
              getRowHeight={() => 'auto'}
              rowsPerPageOptions={[10, 25, 50]}
              autoHeight
              density="comfortable"
            />
          )
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="warning">{translate('marketingPersonsCancel')}</Button>
      </DialogActions>
    </Dialog>
    </LocalizationProvider>
  );
};

export default PersonReportModal;
